import { type FormEvent, useEffect, useState } from 'react';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';
import { formatDate } from '../lib/format.js';

const { t } = i18n;

interface HolidayRow {
  id: string;
  date: string;
  name: string;
  country: string;
}

export function HolidaysPage() {
  const [rows, setRows] = useState<HolidayRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [date, setDate] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    setRows(null);
    api<HolidayRow[]>('/holidays?country=LT')
      .then(setRows)
      .catch((err: unknown) => setError(String(err)));
  }, [reload]);

  async function add(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api('/holidays', { method: 'POST', body: { date, name, country: 'LT' } });
      setDate('');
      setName('');
      setReload((k) => k + 1);
    } catch {
      setError(t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!window.confirm(t('common.confirm_delete'))) return;
    try {
      await api(`/holidays/${id}`, { method: 'DELETE' });
      setReload((k) => k + 1);
    } catch {
      setError(t('common.error'));
    }
  }

  if (error) return <div className="text-sm text-red-600">{error}</div>;

  return (
    <section className="space-y-4 max-w-2xl">
      <h2 className="text-xl font-semibold">{t('holidays.title')}</h2>

      <form onSubmit={(e) => void add(e)} className="flex flex-wrap items-end gap-2 text-sm">
        <label className="block space-y-1">
          <span className="font-medium">{t('holidays.col.date')}</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            className="rounded border border-border bg-background px-2 py-1.5"
          />
        </label>
        <label className="block space-y-1 flex-1 min-w-[200px]">
          <span className="font-medium">{t('holidays.col.name')}</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </label>
        <button
          type="submit"
          disabled={busy || !date || !name}
          className="rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
        >
          {busy ? t('common.loading') : t('holidays.add')}
        </button>
      </form>

      {rows === null ? (
        <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
      ) : rows.length === 0 ? (
        <div className="rounded border border-dashed border-border p-4 text-sm text-muted-foreground">
          {t('holidays.empty')}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left">
              <tr>
                <th scope="col" className="px-3 py-2 font-medium">
                  {t('holidays.col.date')}
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  {t('holidays.col.name')}
                </th>
                <th scope="col" className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-3 py-2 tabular-nums">{formatDate(r.date)}</td>
                  <td className="px-3 py-2">{r.name}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => void remove(r.id)}
                      aria-label={t('common.delete')}
                      className="rounded border border-border px-2 py-1 text-xs"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
